import { CobaltPagination } from '@cae/cobalt-react/components/pagination';
import { CobaltTableContainer } from '@cae/cobalt-react/components/table';
import { CobaltClickableTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconCautionMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCautionMajorSolid';
import { CobaltIconWarningMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconWarningMajorSolid';
import { useInvoicesData } from '@features/invoices/contexts/data/useInvoicesData';
import { useInvoiceNavigation } from '@features/invoices/hooks/use-invoice-navigation/useInvoiceNavigation';
import {
  formatPilotName,
  getDueDateStatus,
  shouldShowDueDateIcon,
  DUE_DATE_STATUS,
} from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import { OutstandingAmountCell } from '../../outstanding-amount-cell/OutstandingAmountCell';
import { TruncatedList } from '../../truncated-list/TruncatedList';
import styles from './InvoicesTableMobile.module.css';

const InvoicesTableMobile = React.memo((): React.ReactElement => {
  const {
    invoices,
    pageIndex,
    pageSize,
    handlePageIndexChange,
    handlePageSizeChange,
  } = useInvoicesData();

  const { navigateToInvoice } = useInvoiceNavigation();

  const handleCardClick = React.useCallback(
    (accountReceivableId: string): void => {
      navigateToInvoice(accountReceivableId);
    },
    [navigateToInvoice]
  );

  return (
    <CobaltTableContainer
      className={styles.mobileList}
      data-testid="invoices-table-mobile"
    >
      <div className={styles.mobileListContent}>
        <p className={`body-02 ${styles.invoiceCount}`}>
          Showing {invoices.data.elements.length} of {invoices.data.total}{' '}
          invoices
        </p>
        {invoices.data.elements.map(item => {
          const dueDateStatus = getDueDateStatus(item.dueDate);
          const issuedOnFormatted = formatDateShort(item.issuedOn);
          const dueDateFormatted = formatDateShort(item.dueDate);
          return (
            <CobaltClickableTile
              key={'invoice-tile-' + item.accountReceivableId}
              className={styles.invoiceCard}
              onClick={() => handleCardClick(item.accountReceivableId)}
            >
              <div className={styles.cardContent}>
                <p className={`${styles.cardTitle} heading-01`}>
                  Invoice # <DataValue value={item.transactionNumber} />
                </p>
                <dl className={styles.cardBody}>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Issued on</dt>
                    <dd className={styles.cardValue}>
                      <DataValue value={issuedOnFormatted} />
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Due date</dt>
                    <dd className={styles.cardValue}>
                      <span className={styles.dueDateValue}>
                        <DataValue value={dueDateFormatted} />
                        {shouldShowDueDateIcon(
                          dueDateStatus,
                          DUE_DATE_STATUS.DUE_SOON,
                          item.outstandingAmount,
                          item.balanceStatus
                        ) && (
                          <CobaltIconCautionMajorSolid
                            className={styles.iconDueSoon}
                            aria-label="Due in the next 7 days"
                          />
                        )}
                        {shouldShowDueDateIcon(
                          dueDateStatus,
                          DUE_DATE_STATUS.PAST_DUE,
                          item.outstandingAmount,
                          item.balanceStatus
                        ) && (
                          <CobaltIconWarningMajorSolid
                            className={styles.iconPastDue}
                            aria-label="Past due"
                          />
                        )}
                      </span>
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Outstanding amount</dt>
                    <dd className={styles.cardValue}>
                      <OutstandingAmountCell
                        amount={item.outstandingAmount}
                        currency={item.currency}
                        balanceStatus={item.balanceStatus}
                        isPastDue={dueDateStatus === DUE_DATE_STATUS.PAST_DUE}
                      />
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Crew member</dt>
                    <dd className={styles.cardValue}>
                      <TruncatedList
                        items={(item.pilotNames ?? [])
                          .map(formatPilotName)
                          .filter(name => name !== '')}
                        limit={3}
                        tooltipPosition="top"
                      />
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Reservation number</dt>
                    <dd className={styles.cardValue}>
                      <TruncatedList
                        items={item.schedulerReservationIds ?? []}
                        limit={2}
                        tooltipPosition="top"
                      />
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>Legal entity</dt>
                    <dd className={styles.cardValue}>
                      <DataValue value={item.legalEntityName} />
                    </dd>
                  </div>
                  <div className={styles.cardRow}>
                    <dt className={styles.cardLabel}>PO number</dt>
                    <dd className={styles.cardValue}>
                      <TruncatedList
                        items={item.poNumbers ?? []}
                        limit={2}
                        tooltipPosition="top"
                      />
                    </dd>
                  </div>
                </dl>
              </div>
            </CobaltClickableTile>
          );
        })}
        <CobaltPagination
          length={invoices.data.total}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageIndexChange={handlePageIndexChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
          hasPageSelect={false}
          hasItemCount={false}
        />
      </div>
    </CobaltTableContainer>
  );
});

InvoicesTableMobile.displayName = 'InvoicesTableMobile';

export { InvoicesTableMobile };
